import { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { useEntity, GROUP_VALUE } from "@/lib/entityStore";
import { Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Empty } from "@/components/ds/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

export default function Notes() {
    const { entities, selectedEntityId } = useEntity();
    const [notes, setNotes] = useState([]);
    const [kinds, setKinds] = useState([]);
    const [includeResolved, setIncludeResolved] = useState(false);
    const [form, setForm] = useState({
        note: "", kind: "general", entity_id: "__none__",
        effective_from: "", effective_to: "", impact_estimate: "",
        tags: "",
    });

    const load = async () => {
        const ent = selectedEntityId === GROUP_VALUE ? "" : `&entity_id=${selectedEntityId}`;
        const { data } = await api.get(`/notes?include_resolved=${includeResolved}${ent}`);
        setNotes(data);
    };

    useEffect(() => {
        load();
        api.get("/notes/kinds").then((r) => setKinds(r.data));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntityId, includeResolved]);

    const submit = async () => {
        if (!form.note.trim()) {
            toast.error("Note text is required");
            return;
        }
        try {
            const body = {
                note: form.note,
                kind: form.kind,
                entity_id: form.entity_id === "__none__" ? null : form.entity_id,
                effective_from: form.effective_from || null,
                effective_to: form.effective_to || null,
                impact_estimate: form.impact_estimate ? Number(form.impact_estimate) : null,
                tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
            };
            await api.post("/notes", body);
            toast.success("Note captured — AI will use this in future answers");
            setForm({ note: "", kind: "general", entity_id: "__none__", effective_from: "", effective_to: "", impact_estimate: "", tags: "" });
            await load();
        } catch {
            toast.error("Could not save note");
        }
    };

    const resolve = async (id) => {
        await api.patch(`/notes/${id}`, { resolved: true });
        await load();
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this note?")) return;
        await api.delete(`/notes/${id}`);
        await load();
    };

    const entityName = (eid) =>
        eid ? entities.find((e) => e.id === eid)?.display_name || "—" : "Group-level";

    return (
        <PageShell width="default" testId="notes-page">
            <PageHeader
                eyebrow="Management memory"
                title="Notes"
                subtitle="Human-supplied business context the system would never infer from the numbers alone — late payments, one-off events, accountant commentary. Active notes flow into the AI's grounding."
            />

            <Card variant="default" padding="lg" className="mb-6" testId="note-create">
                <div className="label-soft text-stone-500 mb-3">Capture business context</div>
                <Textarea
                    rows={2}
                    className="rounded-md mb-4 resize-none"
                    placeholder='e.g. "Major customer payment delayed due to acquisition" / "Workshop expansion compressing Entity 4 margins until June"'
                    value={form.note}
                    onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    data-testid="note-input"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                        <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Kind</Label>
                        <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
                            <SelectTrigger className="rounded-md h-9 mt-1" data-testid="note-kind">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-md">
                                {kinds.map((k) => (
                                    <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Entity</Label>
                        <Select value={form.entity_id} onValueChange={(v) => setForm((f) => ({ ...f, entity_id: v }))}>
                            <SelectTrigger className="rounded-md h-9 mt-1" data-testid="note-entity">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-md">
                                <SelectItem value="__none__">Group-level</SelectItem>
                                {entities.map((e) => (
                                    <SelectItem key={e.id} value={e.id}>{e.display_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Effective from</Label>
                        <Input
                            type="date"
                            className="rounded-md h-9 mt-1"
                            value={form.effective_from}
                            onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))}
                        />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Effective to</Label>
                        <Input
                            type="date"
                            className="rounded-md h-9 mt-1"
                            value={form.effective_to}
                            onChange={(e) => setForm((f) => ({ ...f, effective_to: e.target.value }))}
                        />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Impact ($)</Label>
                        <Input
                            type="number"
                            className="rounded-md h-9 mt-1 font-mono-num"
                            placeholder="-85000"
                            value={form.impact_estimate}
                            onChange={(e) => setForm((f) => ({ ...f, impact_estimate: e.target.value }))}
                            data-testid="note-impact"
                        />
                    </div>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Tags (comma-separated)</Label>
                        <Input
                            className="rounded-md h-9 mt-1"
                            value={form.tags}
                            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                            placeholder="urgent, cashflow, acquisition"
                        />
                    </div>
                    <div className="flex items-end justify-end">
                        <button
                            type="button"
                            onClick={submit}
                            className="inline-flex items-center gap-2 text-[13px] px-4 py-2 rounded-md text-white"
                            style={{ background: "var(--brand)" }}
                            data-testid="note-submit"
                        >
                            Save note
                        </button>
                    </div>
                </div>
            </Card>

            <div className="flex items-center justify-between mb-4">
                <div className="label-soft text-stone-500">
                    {notes.length} {includeResolved ? "total" : "active"} note{notes.length === 1 ? "" : "s"}
                </div>
                <label className="flex items-center gap-2 text-[13px] text-stone-600">
                    <Checkbox
                        checked={includeResolved}
                        onCheckedChange={(v) => setIncludeResolved(!!v)}
                        data-testid="show-resolved"
                    />
                    Include resolved
                </label>
            </div>

            {notes.length === 0 ? (
                <Card variant="default" padding="lg">
                    <Empty title="No notes captured yet" hint="Capture business context above and it will flow into the AI." />
                </Card>
            ) : (
                <Section>
                    <div className="space-y-2">
                        {notes.map((n) => (
                            <Card
                                key={n.id}
                                variant="default"
                                padding="md"
                                className={n.resolved ? "opacity-60" : ""}
                                testId={`note-${n.id.slice(0, 8)}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border rounded-full border-stone-200 bg-stone-50 text-stone-700">
                                                {n.kind.replace(/_/g, " ")}
                                            </span>
                                            <span className="text-[12px] text-stone-500">{entityName(n.entity_id)}</span>
                                            {n.impact_estimate != null && (
                                                <span className={`text-[12px] font-mono-num ${n.impact_estimate < 0 ? "text-red-700" : "text-emerald-700"}`}>
                                                    {n.impact_estimate < 0 ? "" : "+"}${Math.abs(n.impact_estimate).toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[14px] text-stone-800 leading-snug">{n.note}</div>
                                        <div className="mt-2 text-[11px] text-stone-400 font-mono flex gap-2 flex-wrap">
                                            {n.effective_from && <span>from {n.effective_from}</span>}
                                            {n.effective_to && <span>to {n.effective_to}</span>}
                                            {n.tags?.length > 0 && <span>· tags: {n.tags.join(", ")}</span>}
                                            <span className="ml-auto">{fmtDateTime(n.created_at)}</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                        {!n.resolved && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 px-2 text-xs text-stone-600 hover:text-emerald-700"
                                                onClick={() => resolve(n.id)}
                                                data-testid={`resolve-${n.id.slice(0, 8)}`}
                                            >
                                                <CheckCircle2 size={13} />
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2 text-xs text-stone-500 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => remove(n.id)}
                                            data-testid={`delete-${n.id.slice(0, 8)}`}
                                        >
                                            <Trash2 size={13} />
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </Section>
            )}
        </PageShell>
    );
}
